<template>
    <div class="flex flex-column md:p-4 ">
        <div class="flex flex-wrap justify-content-between gap-2 py-2">
            <div class="flex justify-content-start align-items-center gap-2">
                <img :src="profileImage" class="profile-image" alt="Image" />
                <div class="flex flex-column justify-content-start gap-1">
                    <span class="text-left Paragraph-Medium">{{ student.studentName }}</span>
                    <span class="Caption">{{ cleanupLables(student.grade) }}</span>
                    <span class="Caption"> {{ formatClassModels(student.classModels) }}</span>
                </div>
            </div>
            
            <div class="flex align-items-center gap-2">
                <ChatButton :otherUserId="student.studentId" :otherUserName="student.studentName" @open-chat="$emit('open-chat', $event)" />
                <Button label="View Profile" class="teal-button-small" @click="viewProfile" />
                <Menu :model="menuItems" :popup="true" ref="menu" class="menu-item" />
                <Button class="menu-button" @click="toggleMenu" aria-haspopup="true" aria-controls="overlay_menu">
                    <i class="pi pi-ellipsis-v"></i>
                </Button>
            </div>
        </div>
        
        <div class="card p-0">
            <div v-if="viewStack.length > 1 && currentView.type !== 'tabs'" class="mb-4" style="margin-top: 2rem;">
                <Divider class="w-full mb-5" />
                <Button label="Back" icon="pi pi-arrow-left" class="p-button-text back-button cursor-pointer"
                    @click="goBack" />
            </div>
            
            <Tabs v-if="currentView.type === 'tabs'" v-model:value="activeTab">
                <TabList class="flex flex-column justify-content-center align-items-center w-full">
                    <Tab value="0">Classes Info</Tab>
                    <Tab value="1">Results</Tab>
                </TabList>

                <TabPanels class="p-0">
                    <TabPanel value="0">
                        <ClassInfo :classes="classes" :modules="modules"
                            @view-class="pushView('previous-classes', $event)"
                            @view-info="pushView('view-info', $event)"
                            @refresh-classes="fetchUpcomingClasses" />
                    </TabPanel>

                    <TabPanel value="1">
                        <ResultsInfo :results="results" @add-results="pushView('add-results')"
                            @view-result="pushView('view-result', $event)" />
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <div v-else-if="currentView.type === 'previous-classes'">
                <ClassInfo :classes="previousClasses" :modules="modules" :inPrevious="true"
                    @view-class="pushView('previous-classes', $event)" @view-info="pushView('view-info', $event)" />
            </div>

            <div v-else-if="currentView.type === 'add-results'">
                <AddResults @save-result="saveNewResult" @cancel="goBack" />
            </div>

            <div v-else-if="currentView.type === 'edit-result'">
                <AddResults :result="currentView.data" @save-result="updateExistingResult" @cancel="goBack" />
            </div>

            <div v-else-if="currentView.type === 'view-result'">
                <ResultDetails :result="currentView.data" :studentId="student.studentId" @back="goBack"
                    @edit-result="pushView('edit-result', $event)" @refresh-results="handleRefreshAndBack" />
            </div>

            <div v-else-if="currentView.type === 'view-info'">
                <ClassInfoDetails :classInstanceId="currentView.data.id" @back="goBack" />
            </div>
        </div>
    </div>
    <ConfirmDialog></ConfirmDialog>
</template>

<script setup>
import Button from "primevue/button";
import { defineProps, defineEmits, onMounted, ref, computed, watchEffect } from "vue";
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Menu from 'primevue/menu';
import Divider from 'primevue/divider';
import { ConfirmDialog } from "primevue";
import { useConfirm } from "primevue/useconfirm";

import ClassInfo from '@/components/dashboard/tutor/enrolledStudents/classInfo.vue'
import ResultsInfo from '@/components/dashboard/tutor/enrolledStudents/resultsInfo.vue'
import AddResults from "./addResults.vue";
import ResultDetails from "./resultDetails.vue";
import ClassInfoDetails from './classInfoDetails.vue';

import { cleanupLables } from "@/utils/cleanups";
import router from '@/router';
import { notifyContrast, notifyError, notifySuccess } from "@/utils/notification";
import { classesByStudent, createResult, updateResult, deleteClasses, getStudentResults, previousClassesByStudent } from "@/api/tutor";
import blankProfile from '@/assets/blank-profile.png';
import { generateSignedUrl } from "@/api/storage";
import ChatButton from "@/components/ChatButton.vue";

const props = defineProps({
    student: {
        type: Object,
        required: true
    }
});
defineEmits(['open-chat']);
const confirm = useConfirm();
const viewStack = ref([{ type: "tabs", label: "Dashboard" }]);
const currentView = computed(() => viewStack.value[viewStack.value.length - 1]);
const activeTab = ref('0');

function pushView(type, data = null) {
    viewStack.value.push({ type, data });
}

function goBack() {
    if (viewStack.value.length > 1) {
        viewStack.value.pop();
    }
}

const profileImage = ref(blankProfile);

watchEffect(async () => {
    if (props.student?.studentPhotoURL) {
        profileImage.value = await generateSignedUrl(props.student.studentPhotoURL);
    } else {
        profileImage.value = blankProfile;
    }
});

const formatClassModels = (classModels) => {
    if (!classModels) return '';
    const hasIndividual = classModels.includes('Individual Class');
    const hasGroup = classModels.includes('Group Class');
    if (hasIndividual && hasGroup) return 'Individual & Group Classes';
    else if (hasIndividual) return 'Individual Class';
    else if (hasGroup) return 'Group Class';
    return classModels.join(' | ');
};

const classes = ref([]);
const previousClasses = ref([]);
const modules = ref([]);
const results = ref([]);
const menu = ref();

const menuItems = ref([
    { label: 'End classes (Unenroll)', command: () => endCall() }
]);

const toggleMenu = (event) => menu.value.toggle(event);

const endCall = () => {
    confirm.require({
        message: 'Do you want to end classes with this student?',
        header: 'Danger Zone',
        icon: 'pi pi-info-circle',
        rejectLabel: 'Cancel',
        rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
        acceptProps: { label: 'Delete', severity: 'danger' },
        accept: async () => {
            await deleteClasses(props.student.studentId);
            notifySuccess("Success!", `Classes with ` + props.student.studentName + ` have been ended.`);
            location.reload();
        },
        reject: () => { notifyContrast("Cancelled!", "Classes will not be ended."); }
    });
};

const viewProfile = () => {
    router.push('/studentProfile/' + props.student.studentId);
}

const fetchUpcomingClasses = async () => {
    const res = await classesByStudent(props.student.studentId);
    classes.value = res.data.data || [];
    const subjects = [...new Set(classes.value.map(c => c.subject))].sort();
    modules.value = subjects;
}

const fetchPreviousClasses = async () => {
    const res = await previousClassesByStudent(props.student.studentId);
    previousClasses.value = res.data.data || [];
}

const fetchStudentResults = async () => {
    const res = await getStudentResults(props.student.studentId);
    results.value = res.data.data || [];
}

const saveNewResult = async (data) => {
    try {
        await createResult(props.student.studentId, data);
        notifySuccess("Success!", "Results has been submitted!");
        await fetchStudentResults();
        goBack();
    } catch {
        notifyError("Error!", "Please try again later.")
    }
}

const updateExistingResult = async (data) => {
    try {
        await updateResult(data._id, data);
        notifySuccess("Success!", "Results updated successfully!");
        await fetchStudentResults();
        goBack();
        goBack();
    } catch (error) {
        console.error(error);
        notifyError("Error!", "Failed to update result.")
    }
}

const handleRefreshAndBack = async () => {
    await fetchStudentResults();
    goBack();
}

onMounted(async () => {
    fetchUpcomingClasses();
    fetchPreviousClasses();
    fetchStudentResults();
})
</script>

<style scoped>
.teal-button-small {
    padding: 8px 16px;
    height: 30px;
    font-size: 0.75rem;
}

.Card {
    /* background-color: var(--teal-800); */
    border-radius: 4px;
    box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.16);
}

.view {
    padding: 8px 16px 8px 16px;
    height: 30px;
    font-size: 0.75rem;

}

.p-button-danger {
    padding: 10px 16px 10px 16px;
    height: 30px;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}


.marks {
    font-size: 112px;
    font-weight: 700;
    font-family: 'Raleway', sans-serif;
    line-height: 110%;
    color: var(--black-500);
}

.menu-button {
    background: none;
    border: none;
    padding: 8px 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-700) !important;
}

.menu-button:hover {
    background: transparent !important;
    border: none;
    padding: 8px 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-700) !important;
}


.menu-item {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    color: var(--orange-500) !important;
}

.menu-item:hover {
    background-color: #f5f5f5;
}

.menu-item:last-child {
    border-bottom: none;
}

.menu-container:hover .menu-dropdown {
    display: block;
}
</style>